<template>
  <div v-if="visible">
    <aside
      class="tw:hidden tw:md:flex tw:flex-col tw:fixed tw:top-0 tw:bottom-0 tw:left-0 tw:z-40 tw:bg-slate-900 tw:border-r tw:border-slate-800/60 sidebar-transition tw:text-slate-100 desktop-sidebar"
      :class="[
        isPinned ? 'tw:w-[260px] is-pinned' : 'tw:w-[72px]'
      ]"
    >
      <div class="tw:h-16 tw:flex tw:items-center tw:px-5 tw:border-b tw:border-slate-800/40 tw:shrink-0">
        <div
          class="logo-container tw:overflow-hidden tw:shrink-0 tw:transition-all tw:duration-300"
          :class="[
            isPinned ? 'tw:w-[153px]' : 'tw:w-[32px] tw:hover:w-[153px]'
          ]"
        >
          <div class="sidebar-logo-lockup tw:flex tw:items-center tw:gap-2 tw:h-[32px] tw:w-[153px] tw:min-w-[153px] tw:max-w-none">
            <img src="/img/logo-symbol.svg" class="sidebar-logo-symbol tw:w-8 tw:h-8 tw:shrink-0" alt="" aria-hidden="true" />
            <span class="sidebar-logo-wordmark tw:text-2xl tw:font-bold tw:leading-none">fareclock</span>
          </div>
        </div>
      </div>

      <button
        v-if="!editing"
        @click="$emit('toggle-pin')"
        class="tw:hidden tw:md:flex tw:absolute tw:top-16 tw:right-0 tw:translate-x-1/2 tw:w-6 tw:h-6 tw:p-0 tw:rounded-full tw:bg-slate-900 tw:border tw:border-slate-800 tw:hover:border-slate-750 tw:hover:bg-slate-800 tw:text-slate-400 tw:hover:text-slate-100 tw:items-center tw:justify-center tw:shadow-premium tw:cursor-pointer tw:z-50 tw:transition-all tw:duration-200"
        title="Toggle Sidebar Layout"
      >
        <ChevronLeft v-if="isPinned" class="tw:w-3.5 tw:h-3.5" />
        <ChevronRight v-else class="tw:w-3.5 tw:h-3.5" />
      </button>

      <div
        v-if="editing"
        class="tw:px-4 tw:py-3 tw:border-b tw:border-slate-800/40 tw:shrink-0"
      >
        <div class="tw:flex tw:items-center tw:gap-2">
          <span class="tw:flex-1 tw:min-w-0 tw:text-sm tw:font-bold tw:truncate">Customize sidebar</span>
          <button
            v-if="controller.isCustomized.value"
            type="button"
            class="tw:h-8 tw:px-2 tw:rounded-lg tw:text-xs tw:text-slate-400 tw:hover:text-slate-100 tw:hover:bg-slate-800 tw:cursor-pointer"
            title="Go back to the standard sidebar"
            @click="confirmReset"
          >
            Reset
          </button>
          <button
            type="button"
            class="sidebar-done-button tw:h-8 tw:px-3 tw:rounded-lg tw:text-sm tw:font-medium tw:text-white tw:bg-indigo-600 tw:hover:bg-indigo-500 tw:cursor-pointer"
            @click="stopEditing"
          >
            Done
          </button>
        </div>
        <p class="tw:m-0 tw:mt-1 tw:text-xs tw:text-slate-500">
          Drag to reorder. Click an item to rename it or change its icon and color.
        </p>
      </div>

      <nav class="tw:flex-1 tw:py-4 tw:px-3" data-tour="sidebar-nav">
        <div class="tw:space-y-1">
          <SidebarLink
            v-for="link in controller.fixedLinks.value"
            :key="link.id"
            :link="link"
            :organization-id="organizationId"
            variant="desktop"
            :is-pinned="isPinned"
            :active="link.id === activeLinkId && !editing"
            :editing="editing"
            inert
          />
          <SidebarTree
            :controller="controller"
            variant="desktop"
            :is-pinned="isPinned"
            :editing="editing"
            :active-link-id="activeLinkId"
            :organization-id="organizationId"
            :has-items-above="controller.fixedLinks.value.length > 0"
          />
        </div>

        <!--
          Deliberately quiet: it changes the sidebar rather than going anywhere, and is used now and
          then, so it sits after the last item instead of beside the destinations in the footer.
          Not offered on the icon rail -- customizing needs the labels, so pin the sidebar first.
          Hidden there with CSS rather than v-if: the tour points at it, and counts its steps (for
          the invitation) before it pins the sidebar open, so the anchor has to exist either way.
        -->
        <button
          v-if="!editing"
          type="button"
          data-tour="sidebar-customize"
          class="tw:mt-3 tw:items-center tw:h-7 tw:px-3 tw:rounded-lg tw:text-xs tw:text-slate-500 tw:hover:text-slate-200 tw:hover:bg-slate-800/60 tw:transition-colors tw:cursor-pointer"
          :class="isPinned ? 'tw:inline-flex' : 'tw:hidden'"
          @click="startEditing"
        >
          <!-- Icon and text line up with the items' icons and labels above, but the highlight hugs
               the content so it does not read as another page in the list. -->
          <span class="tw:w-6 tw:flex tw:justify-center tw:shrink-0">
            <PanelLeftDashed class="tw:w-3.5 tw:h-3.5" />
          </span>
          <span class="tw:ml-3">Customize sidebar</span>
        </button>
      </nav>

      <div class="tw:p-3 tw:border-t tw:border-slate-800/40 tw:shrink-0 tw:space-y-1">
        <router-link
          v-if="showByxbi"
          :to="{ name: 'byxbi', params: { orgId: organizationId } }"
          class="tw:w-full tw:flex tw:items-center tw:h-11 tw:px-3 tw:rounded-xl tw:transition-all tw:duration-200 tw:group/item tw:relative tw:overflow-hidden tw:text-left tw:cursor-pointer tw:select-none tw:no-underline"
          :class="[
            isByxbiActive
              ? 'tw:bg-indigo-600 tw:text-white tw:font-medium tw:shadow-md tw:shadow-indigo-600/10 tw:hover:bg-indigo-500'
              : 'tw:text-slate-400 tw:hover:text-slate-100 tw:hover:bg-slate-800/90 tw:hover:backdrop-blur-md',
            !isPinned ? 'tw:hover:w-[236px] tw:hover:shadow-premium tw:hover:z-50' : ''
          ]"
        >
          <div
            v-if="isByxbiActive"
            class="tw:absolute tw:left-0 tw:top-3 tw:bottom-3 tw:w-1 tw:rounded-r tw:bg-[#FF5352]"
          ></div>

          <div class="tw:w-6 tw:h-6 tw:flex tw:items-center tw:justify-center tw:shrink-0">
            <FlagPhilippines class="tw:w-5 tw:h-3.5" />
          </div>
          <span
            class="tw:ml-3 tw:text-sm tw:truncate nav-label"
            :class="{
              'tw:opacity-0 tw:translate-x-0 tw:pointer-events-none tw:group-hover/item:opacity-100 tw:group-hover/item:translate-x-0 tw:group-hover/item:pointer-events-auto': !isPinned
            }"
          >
            Byxbi
          </span>
        </router-link>

        <router-link
          :to="{ name: 'settings', params: { orgId: organizationId } }"
          class="tw:w-full tw:flex tw:items-center tw:h-11 tw:px-3 tw:rounded-xl tw:transition-all tw:duration-200 tw:group/item tw:relative tw:overflow-hidden tw:text-left tw:cursor-pointer tw:select-none tw:no-underline"
          :class="[
            isSettingsActive
              ? 'tw:bg-indigo-600 tw:text-white tw:font-medium tw:shadow-md tw:shadow-indigo-600/10 tw:hover:bg-indigo-500'
              : 'tw:text-slate-400 tw:hover:text-slate-100 tw:hover:bg-slate-800/90 tw:hover:backdrop-blur-md',
            !isPinned ? 'tw:hover:w-[236px] tw:hover:shadow-premium tw:hover:z-50' : ''
          ]"
        >
          <div
            v-if="isSettingsActive"
            class="tw:absolute tw:left-0 tw:top-3 tw:bottom-3 tw:w-1 tw:rounded-r tw:bg-[#FF5352]"
          ></div>

          <div class="tw:w-6 tw:h-6 tw:flex tw:items-center tw:justify-center tw:shrink-0">
            <Settings class="tw:w-5 tw:h-5 tw:transition-transform tw:duration-500 tw:group-hover/item:rotate-45" />
          </div>
          <span
            class="tw:ml-3 tw:text-sm tw:truncate nav-label"
            :class="{
              'tw:opacity-0 tw:translate-x-0 tw:pointer-events-none tw:group-hover/item:opacity-100 tw:group-hover/item:translate-x-0 tw:group-hover/item:pointer-events-auto': !isPinned
            }"
          >
            Organization Settings
          </span>
        </router-link>
      </div>
    </aside>

    <div
      v-if="isMobileOpen"
      @click="$emit('update:isMobileOpen', false)"
      class="tw:md:hidden tw:fixed tw:inset-0 tw:z-40 tw:bg-slate-955/60 tw:backdrop-blur-sm tw:transition-opacity"
    ></div>

    <aside
      class="mobile-sidebar tw:md:hidden tw:fixed tw:top-0 tw:bottom-0 tw:left-0 tw:z-50 tw:w-[280px] tw:bg-slate-900 tw:border-r tw:border-slate-800/60 tw:flex tw:flex-col tw:text-slate-100 tw:transition-transform tw:duration-300"
      :class="isMobileOpen ? 'tw:translate-x-0' : 'tw:-translate-x-full'"
    >
      <div class="tw:h-16 tw:flex tw:items-center tw:justify-between tw:px-6 tw:border-b tw:border-slate-800/40 tw:shrink-0">
        <div class="tw:flex tw:items-center tw:gap-3">
          <div class="sidebar-logo-lockup tw:flex tw:items-center tw:gap-2 tw:h-[32px] tw:w-[153px] tw:min-w-[153px]">
            <img src="/img/logo-symbol.svg" class="sidebar-logo-symbol tw:w-8 tw:h-8 tw:shrink-0" alt="" aria-hidden="true" />
            <span class="sidebar-logo-wordmark tw:text-2xl tw:font-bold tw:leading-none">fareclock</span>
          </div>
        </div>
        <button
          @click="$emit('update:isMobileOpen', false)"
          class="tw:w-8 tw:h-8 tw:rounded-lg tw:flex tw:items-center tw:justify-center tw:text-slate-400 tw:hover:text-slate-100 tw:hover:bg-slate-800 tw:transition-colors tw:cursor-pointer"
        >
          <X class="tw:w-5 tw:h-5" />
        </button>
      </div>

      <nav class="tw:flex-1 tw:py-6 tw:px-4 tw:overflow-y-auto">
        <div class="tw:space-y-1">
          <SidebarLink
            v-for="link in controller.fixedLinks.value"
            :key="link.id"
            :link="link"
            :organization-id="organizationId"
            variant="mobile"
            :is-pinned="true"
            :active="link.id === activeLinkId"
            :editing="false"
            @navigate="$emit('update:isMobileOpen', false)"
          />
          <SidebarTree
            :controller="controller"
            variant="mobile"
            :is-pinned="true"
            :editing="false"
            :active-link-id="activeLinkId"
            :organization-id="organizationId"
            :has-items-above="controller.fixedLinks.value.length > 0"
            @navigate="$emit('update:isMobileOpen', false)"
          />
        </div>
      </nav>

      <div class="tw:p-4 tw:border-t tw:border-slate-800/40 tw:shrink-0 tw:space-y-1">
        <router-link
          v-if="showByxbi"
          :to="{ name: 'byxbi', params: { orgId: organizationId } }"
          @click="$emit('update:isMobileOpen', false)"
          class="tw:w-full tw:flex tw:items-center tw:h-12 tw:px-4 tw:rounded-xl tw:transition-all tw:duration-200 tw:group/item tw:relative tw:overflow-hidden tw:text-left tw:cursor-pointer tw:select-none tw:no-underline"
          :class="[
            isByxbiActive
              ? 'tw:bg-indigo-600 tw:text-white tw:font-medium tw:shadow-md tw:shadow-indigo-650/10'
              : 'tw:text-slate-400 tw:hover:text-slate-100 tw:hover:bg-slate-800/50'
          ]"
        >
          <div class="tw:w-6 tw:h-6 tw:flex tw:items-center tw:justify-center tw:shrink-0">
            <FlagPhilippines class="tw:w-5 tw:h-3.5" />
          </div>
          <span class="tw:ml-3 tw:text-sm tw:font-medium">Byxbi</span>
        </router-link>

        <router-link
          :to="{ name: 'settings', params: { orgId: organizationId } }"
          @click="$emit('update:isMobileOpen', false)"
          class="tw:w-full tw:flex tw:items-center tw:h-12 tw:px-4 tw:rounded-xl tw:transition-all tw:duration-200 tw:group/item tw:relative tw:overflow-hidden tw:text-left tw:cursor-pointer tw:select-none tw:no-underline"
          :class="[
            isSettingsActive
              ? 'tw:bg-indigo-600 tw:text-white tw:font-medium tw:shadow-md tw:shadow-indigo-600/10'
              : 'tw:text-slate-400 tw:hover:text-slate-100 tw:hover:bg-slate-800/50'
          ]"
        >
          <div class="tw:w-6 tw:h-6 tw:flex tw:items-center tw:justify-center tw:shrink-0">
            <Settings class="tw:w-5 tw:h-5 tw:transition-transform tw:duration-500 tw:group-hover/item:rotate-45" />
          </div>
          <span class="tw:ml-3 tw:text-sm tw:font-medium">Organization Settings</span>
        </router-link>
      </div>
    </aside>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import { ChevronLeft, ChevronRight, PanelLeftDashed, Settings, X } from '@lucide/vue'
// @ts-expect-error importing untyped JS module into TypeScript
import { hasAccessToRouteName } from '@/router/RouterAccessGuard'
import { useConsoleTourStore } from '@/stores/consoleTour'
// @ts-expect-error importing untyped JS module into TypeScript
import FlagPhilippines from './FlagPhilippines.vue'
import SidebarLink from './SidebarLink.vue'
import SidebarTree from './SidebarTree.vue'
import { useSidebarLayout } from './useSidebarLayout'
import { pickActiveLinkId } from './activeLink'
import { getActiveSection } from './activeSection'

defineProps<{
  isPinned: boolean
  isMobileOpen: boolean
  /** Customize mode. Owned by the page shell, which keeps the sidebar pinned open while it is on. */
  editing: boolean
}>()

const emit = defineEmits<{
  'toggle-pin': []
  'update:isMobileOpen': [open: boolean]
  'update:editing': [editing: boolean]
}>()

const route = useRoute()
const router = useRouter()
const store = useStore()
const toast = useToast()
const confirm = useConfirm()
const consoleTourStore = useConsoleTourStore()

const controller = useSidebarLayout(router, store, {
  onSaveError: () => toast.add({
    severity: 'error',
    summary: 'Sidebar not saved',
    detail: 'Your latest sidebar change could not be saved, so it was undone. Please try again.',
    life: 6000
  })
})

const visible = computed(() => store.getters.insideOrgNamespace)
const organizationId = computed(() => store.state.organizationId)

const activeLinkId = computed(() => pickActiveLinkId(
  [
    ...controller.fixedLinks.value,
    ...controller.entries.value.flatMap(entry => entry.kind === 'section' ? entry.links : [entry])
  ],
  route
))

// The footer links are fixed, and lit only when no customizable item already claims the page --
// someone who added, say, Departments to their sidebar sees that lit rather than Settings too.
const activeSection = computed(() => getActiveSection(route))
const isSettingsActive = computed(() => activeSection.value === 'system' && !activeLinkId.value)
const isByxbiActive = computed(() => activeSection.value === 'byxbi')

const showByxbi = computed(() =>
  !store.state.fatalError && !store.getters.needsSetup && hasAccessToRouteName(router, store, 'byxbi')
)

function startEditing () {
  // The tour shows the standard sidebar while it runs (and points at this button), so
  // customizing then would edit nothing visible.
  if (consoleTourStore.isRunning) {
    return
  }
  emit('update:editing', true)
}

function stopEditing () {
  controller.flush()
  emit('update:editing', false)
}

watch(() => consoleTourStore.isRunning, running => {
  if (running) {
    stopEditing()
  }
})

function confirmReset () {
  confirm.require({
    header: 'Reset sidebar?',
    message: 'Your sections, items and their names, icons and colors will be replaced by the standard sidebar. This cannot be undone.',
    icon: 'pi pi-exclamation-triangle',
    rejectProps: { label: 'Cancel', severity: 'secondary', outlined: true, size: 'small' },
    acceptProps: { label: 'Reset sidebar', severity: 'danger', size: 'small' },
    accept: () => controller.reset()
  })
}
</script>

<style scoped>
.nav-label {
  transition: opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.desktop-sidebar > div:first-of-type {
  padding-left: 20px !important;
  padding-right: 20px !important;
}
.desktop-sidebar nav {
  padding-left: 12px !important;
  padding-right: 12px !important;
  -ms-overflow-style: none !important;
  scrollbar-width: none !important;
}
.desktop-sidebar nav::-webkit-scrollbar {
  display: none !important;
}
.desktop-sidebar.is-pinned nav {
  overflow-y: auto !important;
}
.desktop-sidebar:not(.is-pinned) nav {
  overflow: visible !important;
}
.desktop-sidebar > div:last-of-type {
  padding: 12px !important;
}
.desktop-sidebar > div:last-of-type button {
  padding-left: 12px !important;
  padding-right: 12px !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}
button:focus {
  outline: none !important;
  box-shadow: none !important;
}
button:focus-visible {
  outline: 2px solid var(--color-indigo-500) !important;
  outline-offset: 1px;
}
.desktop-sidebar > button {
  padding: 0 !important;
  width: 24px !important;
  height: 24px !important;
  border: 1px solid var(--color-slate-800) !important;
}
.desktop-sidebar > button:focus,
.desktop-sidebar > button:active {
  outline: none !important;
  box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.7), 0 1px 1px 0 rgba(255, 255, 255, 0.05) inset !important;
}
.desktop-sidebar > button:hover {
  border-color: var(--color-slate-700) !important;
}
</style>
